import React, { useState, useEffect, useRef } from "react";
import { getData, postData, serverURL } from "../../../Services/Fetchnodeservices";

// ─────────────────────────────────────────────────────────────────────────────
// THEME
// ─────────────────────────────────────────────────────────────────────────────
const getT = (isDark) => ({
  bg: isDark ? "#0c1018" : "#f0f4ff",
  surface: isDark ? "#141824" : "#ffffff",
  surfaceAlt: isDark ? "#1c2133" : "#f4f7ff",
  border: isDark ? "rgba(255,255,255,0.07)" : "#e2e8f5",
  text: isDark ? "#f1f5f9" : "#0f172a",
  textMuted: isDark ? "#64748b" : "#94a3b8",
  textSub: isDark ? "#94a3b8" : "#64748b",
  shadow: isDark ? "0 4px 28px rgba(0,0,0,0.45)" : "0 4px 28px rgba(99,102,241,0.10)",
  shadowSm: isDark ? "0 2px 10px rgba(0,0,0,0.35)" : "0 2px 10px rgba(99,102,241,0.07)",
  accent: "#6c63ff",
  success: "#22c55e",
  warning: "#f59e0b",
  danger: "#ef4444",
  info: "#3b82f6",
});

// ─────────────────────────────────────────────────────────────────────────────
// ICONS
// ─────────────────────────────────────────────────────────────────────────────
const UserIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const MailIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const PhoneIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const LockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);

const SendIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

// ─────────────────────────────────────────────────────────────────────────────
// TOAST COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const Toast = ({ message, type, onClose, isDark }) => {
  const t = getT(isDark);
  return (
    <div style={{
      position: "fixed",
      bottom: "24px",
      right: "24px",
      zIndex: 1000,
      background: t.surface,
      border: `1px solid ${type === "error" ? t.danger : t.success}`,
      borderRadius: "12px",
      padding: "14px 20px",
      display: "flex",
      alignItems: "center",
      gap: "14px",
      boxShadow: t.shadow,
      maxWidth: "420px",
      width: "100%",
      animation: "slideUp 0.3s ease"
    }}>
      <div style={{
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        background: type === "error" ? t.danger : t.success,
        flexShrink: 0,
      }} />
      <span style={{ 
        fontSize: "14px", 
        color: type === "error" ? t.danger : t.success,
        flex: 1,
        wordBreak: "break-word",
      }}>
        {message}
      </span>
      <button onClick={onClose} style={{
        background: "none",
        border: "none",
        cursor: "pointer",
        color: t.textMuted,
        padding: "4px",
        fontSize: "18px",
      }}>
        ✕
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const ProfileSettings = ({ isDark = true }) => {
  const t = getT(isDark);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  
  // Audio refs for sound
  const audioRef = useRef(null);
  const continuousAudioRef = useRef(null);

  // ─── Profile State - Dynamic from localStorage ───
  const [profile, setProfile] = useState({
    name: "",
    email: "",
    phone: "",
    role: "",
    role_id: "",
    pic: "",
    id: "",
  });

  // ─── Notification Preferences ───
  const [preferences, setPreferences] = useState([
    { id: 1, label: "Restaurant Profile Update", enabled: true, locked: false },
    { id: 2, label: "New Driver Signup", enabled: true, locked: false },
    { id: 3, label: "Driver Profile Updated", enabled: true, locked: false },
    { id: 4, label: "New User Signup", enabled: true, locked: false },
    { id: 5, label: "New Order Received", enabled: true, locked: false },
    { id: 6, label: "New Website Enquiry", enabled: true, locked: false },
    { id: 7, label: "New Withdrawal Request", enabled: true, locked: false },
    { id: 8, label: "Restaurant Menu Updated", enabled: true, locked: false },
    { id: 9, label: "Restaurant Bank Updated", enabled: true, locked: false },
    { id: 10, label: "Restaurant MOU/Schedule Updated", enabled: true, locked: false },
    { id: 11, label: "New Support Ticket", enabled: true, locked: false },
  ]);

  // ─── Sound & Ringing ───
  const [soundSettings, setSoundSettings] = useState({
    playSound: true,
    alertStyle: "single",
  });

  // ─── Push Notifications ───
  const [devices, setDevices] = useState([
    { id: 1, name: "Edge on Windows", lastUsed: "2026-06-16T08:24:00.000Z" },
    { id: 2, name: "Chrome on Windows", lastUsed: "2026-06-16T08:24:00.000Z" },
    { id: 3, name: "Edge on Windows", lastUsed: "2026-06-16T08:24:00.000Z" },
  ]);

  // ─── Change Password ───
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // ─── Load User Data from localStorage ───
  useEffect(() => {
    const userDataStr = localStorage.getItem('userData');
    if (userDataStr) {
      try {
        const userData = JSON.parse(userDataStr);
        setProfile({
          name: userData.name || "",
          email: userData.email || "",
          phone: userData.contact || userData.phone || "",
          role: userData.role || "",
          role_id: userData.role_id || "",
          pic: userData.pic || "",
          id: userData.id || "",
        });
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, []);

  // ─── Initialize Audio on Mount ───
  useEffect(() => {
    audioRef.current = new Audio("/notification.mp3");
    continuousAudioRef.current = new Audio("/ringtone.mp3");
    continuousAudioRef.current.loop = true;
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (continuousAudioRef.current) {
        continuousAudioRef.current.pause();
        continuousAudioRef.current = null;
      }
    };
  }, []);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ─── Play Sound Function ───
  const playNotificationSound = () => {
    if (!soundSettings.playSound) return;

    try {
      if (soundSettings.alertStyle === "single") {
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
          audioRef.current.play().catch(err => {
            console.log("Audio play failed:", err);
            playFallbackSound();
          });
        } else {
          playFallbackSound();
        }
      } else {
        if (continuousAudioRef.current) {
          continuousAudioRef.current.currentTime = 0;
          continuousAudioRef.current.play().catch(err => {
            console.log("Continuous audio play failed:", err);
            playFallbackSound();
          });
        } else {
          playFallbackSound();
        }
      }
    } catch (error) {
      console.log("Error playing sound:", error);
    }
  };

  const stopContinuousSound = () => {
    if (continuousAudioRef.current) {
      continuousAudioRef.current.pause();
      continuousAudioRef.current.currentTime = 0;
    }
  };

  const playFallbackSound = () => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = "sine";
      
      gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.5);
      
      oscillator.start();
      oscillator.stop(audioCtx.currentTime + 0.5);
    } catch (err) {
      console.log("Fallback sound failed:", err);
    }
  };

  const handleTestSound = () => {
    playNotificationSound();
    showToast("🔔 Test sound playing!");
    
    if (soundSettings.alertStyle === "continuous") {
      setTimeout(() => {
        stopContinuousSound();
      }, 3000);
    }
  };

  // ─── Profile Handlers ───
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      const body = {
        id: profile.id,
        name: profile.name,
        email: profile.email,
        contact: profile.phone,
      };
      
      const res = await postData("Admin/update_profile", body);
      
      if (res && res.status === true) {
        showToast("Profile updated successfully!");
        playNotificationSound();
        
        const userDataStr = localStorage.getItem('userData');
        if (userDataStr) {
          const userData = JSON.parse(userDataStr);
          const updatedData = { ...userData, ...body };
          localStorage.setItem('userData', JSON.stringify(updatedData));
        }
      } else {
        showToast(res?.message || "Failed to update profile", "error");
      }
    } catch (error) {
      showToast("Failed to update profile", "error");
      console.error("Profile update error:", error);
    } finally {
      setLoading(false);
    }
  };

  // ─── Preference Handlers ───
  const togglePreference = (id) => {
    setPreferences(prev =>
      prev.map(pref =>
        pref.id === id && !pref.locked
          ? { ...pref, enabled: !pref.enabled }
          : pref
      )
    );
    playNotificationSound();
  };

  // ─── Sound Handlers ───
  const toggleSound = () => {
    setSoundSettings(prev => ({ ...prev, playSound: !prev.playSound }));
    if (!soundSettings.playSound) {
      setTimeout(() => playNotificationSound(), 100);
    }
  };

  const setAlertStyle = (style) => {
    stopContinuousSound();
    setSoundSettings(prev => ({ ...prev, alertStyle: style }));
  };

  // ─── Password Handlers ───
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
  };

  // ✅ UPDATED: Handle Change Password with API Response
  const handleChangePassword = async () => {
    // Clear previous messages
    setToast(null);
    
    // Validations
    if (!passwordData.currentPassword) {
      showToast("Current password is required", "error");
      return;
    }
    if (passwordData.newPassword.length < 6) {
      showToast("New password must be at least 6 characters", "error");
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showToast("Passwords do not match", "error");
      return;
    }

    setLoading(true);
    try {
      const body = {
        id: profile.id,
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
      };
      
      console.log("📤 Sending password change request:", { 
        id: profile.id, 
        current_password: "******", 
        new_password: "******" 
      });
      
      const res = await postData("login/change_password", body);
      
      console.log("📥 Full API Response:", res);
      
      // ✅ Check response status and show appropriate message
      if (res) {
        // Case 1: Success
        if (res.status === true) {
          showToast(res.message || "✅ Password changed successfully!", "success");
          playNotificationSound();
          
          // Clear form
          setPasswordData({ 
            currentPassword: "", 
            newPassword: "", 
            confirmPassword: "" 
          });
          
          // Optional: Logout user after password change
          // setTimeout(() => {
          //   localStorage.removeItem('userData');
          //   navigate('/login');
          // }, 3000);
          
        } 
        // Case 2: Failure with message
        else if (res.status === false) {
          showToast(res.message || "❌ Failed to change password", "error");
        } 
        // Case 3: Unexpected response format
        else {
          showToast(res.message || "⚠️ Unexpected response from server", "error");
        }
      } else {
        showToast("❌ No response from server", "error");
      }
      
    } catch (error) {
      console.error("❌ Password change error:", error);
      
      // Show specific error message
      let errorMsg = "❌ Server error while changing password";
      if (error.response) {
        errorMsg = error.response.data?.message || errorMsg;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      showToast(errorMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  // ─── Push Notification Handlers ───
  const handleRefreshDevice = () => {
    showToast("Device refreshed successfully!");
    playNotificationSound();
  };

  const handleSendTest = () => {
    showToast("Test notification sent!");
    playNotificationSound();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // ─── Styles ───
  const styles = {
    container: {
      minHeight: "100vh",
      background: t.bg,
      fontFamily: "'Inter', 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
      padding: "24px",
      boxSizing: "border-box",
    },
    wrapper: {
      maxWidth: "1400px",
      margin: "0 auto",
      width: "100%",
    },
    header: {
      marginBottom: "32px",
    },
    title: {
      fontSize: "28px",
      fontWeight: 700,
      color: t.text,
      margin: "0 0 4px 0",
      letterSpacing: "-0.5px",
    },
    subtitle: {
      fontSize: "15px",
      color: t.textMuted,
      margin: "4px 0 0 0",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: "24px",
    },
    card: {
      background: t.surface,
      border: `1px solid ${t.border}`,
      borderRadius: "16px",
      padding: "24px",
      boxShadow: t.shadowSm,
      overflow: "hidden",
      minWidth: 0,
    },
    cardFull: {
      gridColumn: "1 / -1",
    },
    cardTitle: {
      fontSize: "18px",
      fontWeight: 600,
      color: t.text,
      margin: "0 0 4px 0",
    },
    cardSubtitle: {
      fontSize: "14px",
      color: t.textMuted,
      margin: "0 0 20px 0",
      lineHeight: "1.5",
    },
    formGroup: {
      marginBottom: "18px",
    },
    label: {
      display: "block",
      fontSize: "12px",
      fontWeight: 600,
      color: t.textMuted,
      marginBottom: "6px",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    inputWrapper: {
      position: "relative",
      width: "100%",
    },
    inputWithIcon: {
      width: "100%",
      padding: "11px 14px 11px 40px",
      background: t.surfaceAlt,
      border: `1px solid ${t.border}`,
      borderRadius: "10px",
      fontSize: "14px",
      color: t.text,
      outline: "none",
      transition: "all 0.2s",
      fontFamily: "inherit",
      boxSizing: "border-box",
      minHeight: "44px",
    },
    inputIcon: {
      position: "absolute",
      left: "12px",
      top: "50%",
      transform: "translateY(-50%)",
      color: t.textMuted,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      pointerEvents: "none",
    },
    passwordToggle: {
      position: "absolute",
      right: "12px",
      top: "50%",
      transform: "translateY(-50%)",
      background: "none",
      border: "none",
      cursor: "pointer",
      color: t.textMuted,
      padding: "4px",
      fontSize: "18px",
    },
    btn: {
      padding: "11px 24px",
      borderRadius: "10px",
      border: "none",
      fontSize: "14px",
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.2s",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      minHeight: "44px",
      minWidth: "120px",
      fontFamily: "inherit",
      width: "100%",
    },
    btnPrimary: {
      background: t.accent,
      color: "#fff",
      "&:hover": {
        opacity: 0.9,
      },
    },
    btnSecondary: {
      background: t.surfaceAlt,
      color: t.text,
      border: `1px solid ${t.border}`,
      "&:hover": {
        background: t.border,
      },
    },
    btnOutline: {
      background: "transparent",
      color: t.accent,
      border: `1px solid ${t.accent}`,
      "&:hover": {
        background: t.accent,
        color: "#fff",
      },
    },
    toggleRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "10px 0",
      borderBottom: `1px solid ${t.border}`,
      gap: "12px",
    },
    toggleLabel: {
      fontSize: "13px",
      color: t.text,
      fontWeight: 500,
    },
    toggleSubLabel: {
      fontSize: "11px",
      color: t.textMuted,
      marginTop: "2px",
    },
    toggleSwitch: {
      width: "40px",
      height: "22px",
      borderRadius: "11px",
      cursor: "pointer",
      position: "relative",
      transition: "all 0.3s",
      flexShrink: 0,
    },
    toggleKnob: {
      width: "16px",
      height: "16px",
      borderRadius: "50%",
      background: "#fff",
      position: "absolute",
      top: "3px",
      transition: "all 0.3s",
      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    },
    radioGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "10px",
      marginTop: "8px",
    },
    radioLabel: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      cursor: "pointer",
      fontSize: "13px",
      color: t.text,
    },
    radio: {
      width: "16px",
      height: "16px",
      cursor: "pointer",
      accentColor: t.accent,
    },
    deviceItem: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "10px 0",
      borderBottom: `1px solid ${t.border}`,
      gap: "12px",
    },
    deviceName: {
      fontSize: "13px",
      fontWeight: 500,
      color: t.text,
    },
    deviceTime: {
      fontSize: "11px",
      color: t.textMuted,
      marginTop: "2px",
    },
    actionRow: {
      display: "flex",
      gap: "12px",
      marginTop: "16px",
      flexWrap: "wrap",
    },
    lockedBadge: {
      fontSize: "10px",
      color: t.textMuted,
      background: t.surfaceAlt,
      padding: "2px 8px",
      borderRadius: "4px",
      marginLeft: "6px",
      display: "inline-block",
    },
    divider: {
      height: "1px",
      background: t.border,
      margin: "20px 0",
    },
    testSection: {
      marginTop: "16px",
      padding: "14px",
      background: t.surfaceAlt,
      borderRadius: "10px",
    },
    deviceStatus: {
      fontSize: "11px",
      color: t.success,
      fontWeight: 500,
      flexShrink: 0,
    },
    toggleText: {
      flex: 1,
      minWidth: 0,
    },
    toggleContainer: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      flexShrink: 0,
    },
    sectionHeader: {
      display: "flex",
      alignItems: "center",
      marginBottom: "4px",
    },
    sectionIcon: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "40px",
      height: "40px",
      borderRadius: "10px",
      marginRight: "12px",
      flexShrink: 0,
    },
    sectionIconLock: {
      background: "linear-gradient(135deg, #f59e0b25, #f59e0b10)",
      color: "#f59e0b",
    },
    sectionIconSound: {
      background: "linear-gradient(135deg, #3b82f625, #3b82f610)",
      color: "#3b82f6",
    },
    sectionIconProfile: {
      background: "linear-gradient(135deg, #6c63ff25, #6c63ff10)",
      color: "#6c63ff",
    },
    // Upper Profile Section
    upperProfile: {
      display: "flex",
      alignItems: "center",
      gap: "14px",
      padding: "10px 18px",
      background: t.surfaceAlt,
      borderRadius: "12px",
      border: `1px solid ${t.border}`,
      marginBottom: "32px",
      transition: "all 0.2s",
      minHeight: "60px",
    },
    profileAvatar: {
      width: "42px",
      height: "42px",
      borderRadius: "50%",
      objectFit: "cover",
      border: `2px solid ${t.accent}`,
      boxShadow: `0 2px 12px ${t.accent}25`,
      flexShrink: 0,
    },
    userInfo: {
      display: "flex",
      alignItems: "center",
      flex: 1,
      minWidth: 0,
      overflow: "hidden",
    },
    userId: {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      fontSize: "14px",
      fontWeight: 500,
      color: t.text,
      flexWrap: "wrap",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    idText: {
      fontWeight: 700,
      color: t.text,
    },
    roleBadge: {
      fontSize: "11px",
      fontWeight: 600,
      color: "#fff",
      background: t.accent,
      padding: "2px 10px",
      borderRadius: "20px",
      display: "inline-block",
      margin: "0 4px",
    },
    separator: {
      color: t.textMuted,
      margin: "0 4px",
      fontSize: "14px",
    },
    nameText: {
      color: t.text,
      fontWeight: 500,
    },
    emailText: {
      color: t.textMuted,
      fontSize: "13px",
    },
  };

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      
      <div style={styles.wrapper}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>⚙️ Profile Settings</h1>
          <p style={styles.subtitle}>Manage your profile, notifications, and security settings</p>
        </div>

        {/* Upper Profile Section */}
        <div style={styles.upperProfile}>
          <img 
            src={profile.pic || `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.name || "User")}&background=6c63ff&color=fff&size=80`} 
            alt={profile.name || "Profile"} 
            style={styles.profileAvatar}
          />
          
          <div style={styles.userInfo}>
            <div style={styles.userId}>
              <span style={styles.idText}>ID: #{profile.id || "N/A"}</span>
              <span style={styles.roleBadge}>{profile.role || "User"}</span>
              <span style={styles.separator}>•</span>
              <span style={styles.nameText}>{profile.name || "No name set"}</span>
              <span style={styles.separator}>•</span>
              <span style={styles.emailText}>{profile.email || "No email"}</span>
            </div>
          </div>
        </div>

        {/* Grid - 3 columns for top row */}
        <div style={styles.grid}>
          {/* ─── PROFILE CARD ─── */}
          <div style={styles.card}>
            <div style={styles.sectionHeader}>
              <div style={{ ...styles.sectionIcon, ...styles.sectionIconProfile }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              <h3 style={styles.cardTitle}>Edit Profile</h3>
            </div>
            <p style={styles.cardSubtitle}>Update your personal information</p>

            <div style={styles.formGroup}>
              <label style={styles.label}>Name</label>
              <div style={styles.inputWrapper}>
                <span style={styles.inputIcon}><UserIcon /></span>
                <input
                  type="text"
                  name="name"
                  value={profile.name}
                  onChange={handleProfileChange}
                  style={styles.inputWithIcon}
                  placeholder="Enter your name"
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Email</label>
              <div style={styles.inputWrapper}>
                <span style={styles.inputIcon}><MailIcon /></span>
                <input
                  type="email"
                  name="email"
                  value={profile.email}
                  onChange={handleProfileChange}
                  style={styles.inputWithIcon}
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Phone</label>
              <div style={styles.inputWrapper}>
                <span style={styles.inputIcon}><PhoneIcon /></span>
                <input
                  type="tel"
                  name="phone"
                  value={profile.phone}
                  onChange={handleProfileChange}
                  style={styles.inputWithIcon}
                  placeholder="Enter your phone number"
                />
              </div>
            </div>

            <button
              style={{ ...styles.btn, ...styles.btnPrimary }}
              onClick={handleSaveProfile}
              disabled={loading}
            >
              {loading ? "Saving..." : "💾 Save Profile"}
            </button>
          </div>

          {/* ─── CHANGE PASSWORD ─── */}
          <div style={styles.card}>
            <div style={styles.sectionHeader}>
              <div style={{ ...styles.sectionIcon, ...styles.sectionIconLock }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  <circle cx="12" cy="16" r="1" fill="currentColor" />
                </svg>
              </div>
              <h3 style={styles.cardTitle}>Change Password</h3>
            </div>
            <p style={styles.cardSubtitle}>Update your password to keep your account secure</p>

            <div style={styles.formGroup}>
              <label style={styles.label}>Current Password</label>
              <div style={styles.inputWrapper}>
                <span style={styles.inputIcon}><LockIcon /></span>
                <input
                  type={showPassword.current ? "text" : "password"}
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  style={styles.inputWithIcon}
                  placeholder="Enter current password"
                />
                <button
                  style={styles.passwordToggle}
                  onClick={() => togglePasswordVisibility("current")}
                  type="button"
                >
                  {showPassword.current ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>New Password</label>
              <div style={styles.inputWrapper}>
                <span style={styles.inputIcon}><LockIcon /></span>
                <input
                  type={showPassword.new ? "text" : "password"}
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  style={styles.inputWithIcon}
                  placeholder="Enter new password (min 6 chars)"
                />
                <button
                  style={styles.passwordToggle}
                  onClick={() => togglePasswordVisibility("new")}
                  type="button"
                >
                  {showPassword.new ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Confirm New Password</label>
              <div style={styles.inputWrapper}>
                <span style={styles.inputIcon}><LockIcon /></span>
                <input
                  type={showPassword.confirm ? "text" : "password"}
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  style={styles.inputWithIcon}
                  placeholder="Confirm new password"
                />
                <button
                  style={styles.passwordToggle}
                  onClick={() => togglePasswordVisibility("confirm")}
                  type="button"
                >
                  {showPassword.confirm ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>
            </div>

          

            <button
              style={{ ...styles.btn, ...styles.btnPrimary }}
              onClick={handleChangePassword}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span style={{ 
                    display: "inline-block", 
                    animation: "spin 1s linear infinite",
                    marginRight: "8px"
                  }}>⟳</span>
                  Changing...
                </>
              ) : (
                "🔑 Change Password"
              )}
            </button>
          </div>

          {/* ─── SOUND & RINGING ─── */}
          <div style={styles.card}>
            <div style={styles.sectionHeader}>
              <div style={{ ...styles.sectionIcon, ...styles.sectionIconSound }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                </svg>
              </div>
              <h3 style={styles.cardTitle}>Sound & Ringing</h3>
            </div>
            <p style={styles.cardSubtitle}>
              Plays when a notification arrives while you have this tab open.
            </p>

            <div style={styles.toggleRow}>
              <div style={styles.toggleText}>
                <div style={styles.toggleLabel}>Play sound</div>
                <div style={styles.toggleSubLabel}>
                  {soundSettings.playSound ? "🔊 Sound is ON" : "🔇 Sound is OFF"}
                </div>
              </div>
              <div style={styles.toggleContainer}>
                <div
                  style={{
                    ...styles.toggleSwitch,
                    background: soundSettings.playSound ? t.success : t.textMuted,
                    cursor: "pointer",
                  }}
                  onClick={toggleSound}
                >
                  <div
                    style={{
                      ...styles.toggleKnob,
                      left: soundSettings.playSound ? "21px" : "3px",
                    }}
                  />
                </div>
              </div>
            </div>

            <div style={{ marginTop: "16px" }}>
              <label style={styles.label}>Alert Style</label>
              <div style={styles.radioGroup}>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="alertStyle"
                    value="single"
                    checked={soundSettings.alertStyle === "single"}
                    onChange={() => setAlertStyle("single")}
                    style={styles.radio}
                  />
                  🔔 Single bell — One short chime
                </label>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="alertStyle"
                    value="continuous"
                    checked={soundSettings.alertStyle === "continuous"}
                    onChange={() => setAlertStyle("continuous")}
                    style={styles.radio}
                  />
                  🔕 Continuous ring — Loops until dismissed
                </label>
              </div>
            </div>

            <div style={styles.testSection}>
              <button
                style={{ ...styles.btn, ...styles.btnOutline }}
                onClick={handleTestSound}
                disabled={!soundSettings.playSound}
              >
                🔊 Test Sound
              </button>
              {soundSettings.alertStyle === "continuous" && (
                <button
                  style={{ ...styles.btn, ...styles.btnSecondary, marginTop: "8px" }}
                  onClick={stopContinuousSound}
                >
                  ⏹ Stop Ringing
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Second row - Full width cards */}
        <div style={{ ...styles.grid, marginTop: "24px" }}>
          {/* ─── NOTIFICATION PREFERENCES ─── */}
          <div style={{ ...styles.card, ...styles.cardFull }}>
            <h3 style={styles.cardTitle}>🔔 Notification Preferences</h3>
            <p style={styles.cardSubtitle}>
              Switch individual event types on or off. Locked (🔒) events are blocked by your role.
            </p>

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "4px 32px",
            }}>
              {preferences.map((pref) => (
                <div key={pref.id} style={styles.toggleRow}>
                  <div style={styles.toggleText}>
                    <div style={styles.toggleLabel}>
                      {pref.label}
                      {pref.locked && <span style={styles.lockedBadge}>🔒 Locked</span>}
                    </div>
                    {pref.locked && (
                      <div style={styles.toggleSubLabel}>Contact admin to grant permission</div>
                    )}
                  </div>
                  <div style={styles.toggleContainer}>
                    <div
                      style={{
                        ...styles.toggleSwitch,
                        background: pref.enabled ? t.success : t.textMuted,
                        opacity: pref.locked ? 0.5 : 1,
                        cursor: pref.locked ? "not-allowed" : "pointer",
                      }}
                      onClick={() => togglePreference(pref.id)}
                    >
                      <div
                        style={{
                          ...styles.toggleKnob,
                          left: pref.enabled ? "21px" : "3px",
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ─── PUSH NOTIFICATIONS ─── */}
          <div style={{ ...styles.card, ...styles.cardFull }}>
            <h3 style={styles.cardTitle}>📱 Push Notifications</h3>
            <p style={styles.cardSubtitle}>
              Receive events like new orders, signups, and enquiries. Which events you get is
              controlled by your role's notification permissions.
            </p>

            <div style={styles.actionRow}>
              <button
                style={{ ...styles.btn, ...styles.btnSecondary, width: "auto", minWidth: "160px" }}
                onClick={handleRefreshDevice}
              >
                <RefreshIcon /> Refresh this device
              </button>
              <button
                style={{ ...styles.btn, ...styles.btnPrimary, width: "auto", minWidth: "160px" }}
                onClick={handleSendTest}
              >
                <SendIcon /> Send test
              </button>
            </div>

            <div style={styles.divider} />

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "8px",
            }}>
              {devices.map((device) => (
                <div key={device.id} style={styles.deviceItem}>
                  <div>
                    <div style={styles.deviceName}>{device.name}</div>
                    <div style={styles.deviceTime}>
                      Last used: {formatDate(device.lastUsed)}
                    </div>
                  </div>
                  <span style={styles.deviceStatus}>● Online</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          isDark={isDark}
        />
      )}
    </div>
  );
};

export default ProfileSettings;